/**
 * LE RAPPORT UNIQUE — chantier « manuel vivant », Φ-MV-5 (MV-TUT-11, MV-DEF-25).
 * `/eleves/:id/manuel/rapport`.
 *
 * Une lecture : `GET /v1/tuteur/manuel/eleves/:eleveId/rapport`. C'est LE
 * document que la famille a reçu en dernier — même identifiant, même contenu,
 * mêmes contrôles que ce que le parent lit. Il n'y a pas de rapport « du
 * tuteur » : un second format ferait deux vérités sur le même enfant.
 *
 * ─── Le défaut que cette page referme (MV-DEF-25) ───────────────────────────
 *
 * `RapportPage` affichait le rapport dont le PLAN ACTIF est issu — le bilan de
 * départ —, pas celui que la famille vient de recevoir. Capacité allumée,
 * `/rapports/:eleveId` renvoie ici : une seule page, un seul document.
 *
 * ─── Ce que l'écran fait du document ────────────────────────────────────────
 *
 * RIEN. Il est composé par le serveur (`DocumentFamille`) et posé tel quel, à
 * la mesure de lecture d'un texte. La section « Le chapitre en cours » n'existe
 * que si le FOYER de l'élève a la capacité et que l'élève a un objectif : clé
 * absente, section absente — sans phrase de remplacement. Aucun rapport :
 * l'écran le dit en clair, et n'affiche rien à la place.
 *
 * Lecture seule : le mot du tuteur et l'envoi ne sont pas de cette tranche, et
 * aucun champ inactif ne les annonce. Aucun bouton plein.
 */
import {
  denombrer,
  type ChapitreDuRapportDto,
  type EtatCaseFrise,
  type EtatPreparation,
  type RapportParentDto,
} from '@silex/shared';
import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { api } from '../api/client';
import { Badge } from '../components/Badge';
import { CransNotion } from '../components/CransNotion';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { SectionRabattable } from '../components/SectionRabattable';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { adressesManuelEleve } from '../lib/manuel-tuteur-format';
import type { BadgeTone } from '../lib/suivi-format';
import { formatDate } from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';
import { useTuteur } from '../tuteur/TuteurContext';

/** Jamais de rouge : « à renforcer » est en ardoise. */
const TON_PREPARATION: Readonly<Record<EtatPreparation, BadgeTone>> = {
  pret: 'acquis',
  en_bonne_voie: 'en-cours',
  a_renforcer: 'a-revoir',
  a_commencer: 'a-revoir',
};

/** Où la CLASSE en est sur une notion — une position dans son calendrier, pas une mesure de l'élève. */
const EN_CLASSE: Readonly<Record<EtatCaseFrise, string>> = {
  vue: 'Vue en classe',
  ici: 'En classe en ce moment',
  a_venir: 'À venir en classe',
};

export function RapportUniquePage() {
  const { id } = useParams<{ id: string }>();
  const actif = manuelVivantActif();
  const { eleves } = useTuteur();
  const resource = useAsync(
    useMemo(() => (actif && id ? () => api.manuelRapport(id) : null), [actif, id]),
  );

  if (!actif) return <Navigate to="/" replace />;
  if (!id) return <Navigate to="/eleves" replace />;

  /* Sans rapport, la réponse ne nomme pas l'élève : son prénom vient du périmètre du tuteur. */
  const suivi =
    eleves.status === 'ready' ? eleves.data.eleves.find((e) => e.eleveId === id) : undefined;
  const rapport = resource.status === 'ready' ? resource.data.rapport : null;
  const prenom = rapport?.elevePrenom ?? suivi?.prenom ?? null;

  return (
    <div className="content__inner">
      <p className="note-line fil-retour">
        <Link to={adressesManuelEleve.fiche(id)}>← Son chapitre</Link>
      </p>
      <div className="dash-topbar">
        <div>
          <h1 data-testid="rapport-unique-titre">
            {prenom === null ? 'Son rapport' : `Rapport de ${prenom}`}
          </h1>
          {rapport === null ? null : (
            <p className="page-sub" data-testid="rapport-unique-date">
              {direEnvoi(rapport)}
            </p>
          )}
        </div>
      </div>

      {resource.status === 'loading' ? <Chargement quoi="le rapport" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}
      {resource.status === 'ready' && rapport === null ? (
        <div data-testid="rapport-unique-absent">
          <EmptyState title="Aucun rapport n’a encore été établi.">
            {prenom === null ? 'La famille' : `La famille de ${prenom}`} n’a reçu aucun document. Le
            premier s’affichera ici, tel qu’elle le lira.
          </EmptyState>
        </div>
      ) : null}
      {rapport === null ? null : <Rapport rapport={rapport} />}
    </div>
  );
}

/** « Établi le 5 octobre · du 28 septembre au 4 octobre · remis à la famille le 5 octobre ». */
function direEnvoi(r: RapportParentDto): string {
  return [
    `Établi le ${formatDate(r.genereLe)}`,
    r.document.periode,
    r.envoyeLe === null
      ? 'pas encore remis à la famille'
      : `remis à la famille le ${formatDate(r.envoyeLe)}`,
  ]
    .filter((m): m is string => m !== null && m !== '')
    .join(' · ');
}

/** Le document, une fois lu : sans appel, il s'éprouve par son rendu. */
export function Rapport({ rapport }: { readonly rapport: RapportParentDto }) {
  const { document } = rapport;
  const echecs = document.controles.filter((c) => !c.passe).length;
  return (
    <div className="stack">
      <section className="card rapport-carte" data-testid="rapport-unique-document">
        <span className="stage-label">Ce que la famille lit, exactement</span>
        <div className="rapport-doc">
          <p className="phrase-parent">{document.phrase}</p>
          {document.contexte ? <p>{document.contexte}</p> : null}

          {document.solide.length > 0 ? (
            <>
              <h4>{document.titreSolide}</h4>
              {document.solide.map((s) => (
                <p key={s.savoirId}>{s.enonce}</p>
              ))}
            </>
          ) : null}

          {document.travaille.length > 0 ? (
            <>
              <h4>{document.titreTravaille}</h4>
              {document.travaille.map((s) => (
                <p key={s.savoirId}>{s.enonce}</p>
              ))}
            </>
          ) : null}

          {document.prochaineEtape ? (
            <>
              <h4>{document.titreProchaineEtape}</h4>
              <p>{document.prochaineEtape}</p>
            </>
          ) : null}

          {document.chapitre === undefined ? null : (
            <ChapitreEnCours chapitre={document.chapitre} />
          )}

          {document.reperesQuebec.length > 0 ? (
            <>
              <h4>Ce que ça veut dire au Québec</h4>
              {document.reperesQuebec.map((r) => (
                <p key={r}>{r}</p>
              ))}
            </>
          ) : null}

          <hr className="sep-soft" />
          <p className="mention-legale">{document.mentionLegale}</p>
        </div>
      </section>

      <div data-testid="rapport-unique-controles">
        <SectionRabattable
          titre="Contrôles du document"
          resume={
            echecs === 0
              ? `${denombrer(document.controles.length, 'contrôle')}, tous réussis`
              : `${denombrer(echecs, 'contrôle')} en échec sur ${document.controles.length}`
          }
          sous="Exécutés par le serveur sur le document ci-dessus, celui que la famille lit."
          ouverteParDefaut={false}
          cleMemoire="manuel.rapport.controles"
        >
          <div className="stack-sm controles">
            {document.controles.map((c) => (
              <div key={c.id} className={c.passe ? 'controle is-ok' : 'controle is-ko'}>
                <span className="controle__marque" aria-hidden="true" />
                <span>
                  <span className="sr-only">
                    {c.passe ? 'Contrôle réussi : ' : 'Contrôle en échec : '}
                  </span>
                  {c.libelle}
                  {!c.passe && c.extrait ? (
                    <span className="controle__extrait">« {c.extrait} »</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </SectionRabattable>
      </div>
    </div>
  );
}

/** « Le chapitre en cours » — la section que le chapitre ajoute au document (MV-TUT-11). */
function ChapitreEnCours({ chapitre: c }: { readonly chapitre: ChapitreDuRapportDto }) {
  return (
    <div data-testid="rapport-unique-chapitre">
      <h4>{c.titre}</h4>
      <p>
        <strong>{c.chapitre}.</strong> {c.objectif} {c.classe}
      </p>
      <div className="list list--programme">
        {c.notions.map((n) => (
          <div key={`${n.rang ?? ''}-${n.titre}`} className="li">
            <span className="li-main">
              <span className="li-title">{n.titre}</span>
              {n.enClasse === null ? null : <span className="li-sub">{EN_CLASSE[n.enClasse]}</span>}
            </span>
            {n.travaillee ? (
              <CransNotion palier={n.palier} libelle={n.libellePalier} />
            ) : (
              <span className="cell-sub">Pas encore travaillée</span>
            )}
          </div>
        ))}
      </div>
      {c.evaluations.map((e) => (
        <p key={`${e.titre}-${e.date}`} data-testid="rapport-unique-evaluation">
          {e.phrase} <Badge tone={TON_PREPARATION[e.etat]} label={e.libelleEtat} />
        </p>
      ))}
      <p className="pal-legend">{c.mention}</p>
    </div>
  );
}
